import { useId, useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';

interface HelpTipProps {
  children: ReactNode;
  className?: string;
  /** Texto corto en hover nativo (primera línea del contenido si se omite). */
  title?: string;
}

export function HelpTip({ children, className = '', title }: HelpTipProps) {
  const [open, setOpen] = useState(false);
  const id = useId();

  return (
    <span className={`relative inline-flex align-middle ${className}`}>
      <button
        type="button"
        className="inline-flex rounded-full p-0.5 text-gray-400 transition-colors hover:bg-sky-50 hover:text-stocker-primary focus:outline-none focus:ring-2 focus:ring-stocker-primary/40"
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        title={title}
        onClick={() => setOpen((v) => !v)}
        onBlur={(e) => {
          if (!e.currentTarget.parentElement?.contains(e.relatedTarget as Node | null)) {
            setOpen(false);
          }
        }}
      >
        <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
      </button>
      {open ? (
        <div
          id={id}
          role="tooltip"
          className="absolute left-1/2 top-full z-50 mt-1.5 w-80 max-w-[min(20rem,calc(100vw-2rem))] -translate-x-1/2 rounded-lg border border-gray-200 bg-white p-3 text-left text-xs font-normal leading-relaxed text-gray-700 shadow-lg"
        >
          {children}
        </div>
      ) : null}
    </span>
  );
}

interface LabelWithHelpProps {
  label: ReactNode;
  help: ReactNode;
  className?: string;
  required?: boolean;
}

/** Etiqueta de campo con icono de ayuda al lado. */
export function LabelWithHelp({ label, help, className = '', required }: LabelWithHelpProps) {
  return (
    <span className={`inline-flex flex-wrap items-center gap-1 ${className}`}>
      <span>
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </span>
      <HelpTip>{help}</HelpTip>
    </span>
  );
}
